import {useMemo, useEffect} from "react";
import {Rocket} from "@phosphor-icons/react";
import {useNavigate} from "react-router-dom";
import fallbackImage from "@/assets/images/fallback-image.svg";
import {GameCarouselRow} from "@/components";
import ImprovedConditionalPaymentProfileCard from "@/components/ImprovedConditionalPaymentProfileCard";
import ProtectedRoute from "@/components/ProtectedRoute";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import {CrashGamesBanners} from "./components/CrashGamesBanners";
import {
  gameCardCls,
  gameCardImageCls,
  gameCardNameCls,
  gameCardOverlayCls,
  gameCardPlayBtnCls,
} from "../Games/components/List/components/_gameCardShared";

// ─── keyword matchers ─────────────────────────────────────────────────────────
const CHICKEN_KEYWORDS = ["chicken", "aviator", "pollo", "gallina", "hawk", "bird"];
const PLINKO_KEYWORDS = ["plinko"];
const RASCA_KEYWORDS = ["rasca", "scratch", "corona", "rascaycorona", "rasca y corona"];
const CRASH_KEYWORDS = ["crash", "rocketman", "rocket", "spaceman", "jetx", "turbo", "ballista", "balista", "mine", "minas", "dice", "dado", "limbo", "tower"];

const matchesAny = (name: string, keywords: string[]) => {
  const lower = (name ?? "").toLowerCase();
  return keywords.some(k => lower.includes(k));
};

// ─── card ─────────────────────────────────────────────────────────────────────
type Game = {
  id: string;
  name: string;
  provider: string;
  icon?: string;
};

type GameCardProps = {
  game: Game;
  onPlay: (provider: string, id: string) => void;
};

const CrashGameCard = ({game, onPlay}: GameCardProps) => (
  <div
    className={gameCardCls}
    role="button"
    tabIndex={0}
    aria-label={`Jugar ${game.name}`}
    onClick={() => onPlay(game.provider, game.id)}
    onKeyDown={e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onPlay(game.provider, game.id);
      }
    }}
  >
    <img
      src={game.icon || fallbackImage}
      alt={game.name ?? ""}
      className={gameCardImageCls}
      onError={e => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = fallbackImage;
      }}
    />
    <div className={gameCardOverlayCls}>
      <span className={gameCardNameCls}>{game.name}</span>
      <button
        type="button"
        className={gameCardPlayBtnCls}
        onClick={ev => {
          ev.stopPropagation();
          onPlay(game.provider, game.id);
        }}
        tabIndex={-1}
      >
        Jugar Ya
      </button>
    </div>
  </div>
);

// ─── page ─────────────────────────────────────────────────────────────────────
const CrashGames = () => {
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const getMiniGames = useMiniGames(state => state.getMiniGames);
  const resetFilters = useMiniGames(state => state.resetFilters);
  const allGames = useMiniGames(state => state.list) ?? [];
  const loading = useMiniGames(state => state.loading);

  useEffect(() => {
    getMiniGames("all", "crash");
    return () => {
      resetFilters();
    };
  }, [getMiniGames, resetFilters]);

  const handlePlay = (provider: string, gameId: string) => {
    if (authStatus === "authenticated") {
      navigate(`/games/${encodeURIComponent(provider)}/${encodeURIComponent(gameId)}`);
    } else {
      handleShowAuthModal();
    }
  };

  const cards = (games: Game[]) =>
    games.map(g => (
      <CrashGameCard
        key={`${g.provider}-${g.id}`}
        game={g}
        onPlay={handlePlay}
      />
    ));

  const chickenGames = useMemo(
    () => allGames.filter(g => matchesAny(g.name, CHICKEN_KEYWORDS)),
    [allGames]
  );

  const plinkoGames = useMemo(
    () => allGames.filter(g => matchesAny(g.name, PLINKO_KEYWORDS)),
    [allGames]
  );

  const rascaGames = useMemo(
    () => allGames.filter(g => matchesAny(g.name, RASCA_KEYWORDS)),
    [allGames]
  );

  const usedIds = useMemo(() => {
    const all = [...chickenGames, ...plinkoGames, ...rascaGames];
    return new Set(all.map(g => `${g.provider}-${g.id}`));
  }, [chickenGames, plinkoGames, rascaGames]);

  const otherCrashGames = useMemo(
    () =>
      allGames.filter(g => {
        const key = `${g.provider}-${g.id}`;
        return !usedIds.has(key) && matchesAny(g.name, CRASH_KEYWORDS);
      }),
    [allGames, usedIds]
  );

  const allCrashFallback = useMemo(
    () => allGames.filter(g => !usedIds.has(`${g.provider}-${g.id}`)).slice(0, 30),
    [allGames, usedIds]
  );

  const hasAnyContent =
    chickenGames.length > 0 ||
    plinkoGames.length > 0 ||
    rascaGames.length > 0 ||
    otherCrashGames.length > 0;

  return (
    <ProtectedRoute>
      <ImprovedConditionalPaymentProfileCard />
      <CrashGamesBanners />
      <div className="w-full flex flex-col gap-10 px-4 min-[768px]:px-8 min-[1200px]:px-[130px] py-6">

        {/* ── Page header ── */}
        <div className="flex flex-col gap-1">
          <h1 className="font-[Oswald,sans-serif] font-bold text-[var(--color-text)] text-3xl max-[640px]:text-2xl flex items-center gap-3">
            <Rocket size={32} weight="fill" className="text-[var(--color-primary)] shrink-0" />
            Coronando con los mejores minijuegos
          </h1>
          <p className="text-[var(--color-text-secondary)] text-sm">
            Los mejores crash games del mundo, te los tenemos
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <span className="text-[var(--color-text-secondary)] text-sm">Cargando juegos...</span>
          </div>
        )}

        {!loading && (
          <>
            {/* ── Chicken Games ── */}
            {chickenGames.length > 0 && (
              <GameCarouselRow
                title="Chicken Games"
                subtitle="Apuesta y multiplica antes de que vuele"
              >
                {cards(chickenGames)}
              </GameCarouselRow>
            )}

            {/* ── Plinko ── */}
            {plinkoGames.length > 0 && (
              <GameCarouselRow
                title="Plinko"
                subtitle="Deja caer la bola y gana"
              >
                {cards(plinkoGames)}
              </GameCarouselRow>
            )}

            {/* ── Rasca y Corona ── */}
            {rascaGames.length > 0 && (
              <GameCarouselRow
                title="Rasca y Corona"
                subtitle="Rasca y descubre tu premio al instante"
              >
                {cards(rascaGames)}
              </GameCarouselRow>
            )}

            {/* ── Otros Minijuegos ── */}
            {otherCrashGames.length > 0 && (
              <GameCarouselRow
                title="Otros Minijuegos"
                subtitle="Crash, Mines, Dice y más"
              >
                {cards(otherCrashGames)}
              </GameCarouselRow>
            )}

            {/* ── Fallback: todos los crash ── */}
            {!hasAnyContent && allCrashFallback.length > 0 && (
              <GameCarouselRow
                title="Todos los Crash Games"
                subtitle="Los mejores minijuegos de la plataforma"
              >
                {cards(allCrashFallback)}
              </GameCarouselRow>
            )}

            {!loading && allGames.length === 0 && (
              <div className="flex items-center justify-center py-20">
                <span className="text-[var(--color-text-secondary)] text-sm">
                  No hay juegos disponibles en este momento.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default CrashGames;
